import qs from 'qs';

import { client } from '@api/client';
import { URL } from '@api/urls';

import { TNameTitle } from '@interfaces/common/common';
import { IGroupOperationDTO } from '@interfaces/common/defaultDTO';
import { IDefaultUploadUrlApi } from '@interfaces/common/file';
import { IItemsResponse } from '@interfaces/common/response';
import {
	ICheckIsTaskExistsDTO,
	ICreateTaskDTO,
	IDeleteTasksDTO,
	IEditTaskDTO,
	IGetPortalTasksDTO,
	IGetTasksToLinkDTO,
	IPortalAvailableMilestonesDTO,
	IPortalTaskDTO,
	IPortalTaskToLinkDTO,
} from '@interfaces/portal/dto/taskManagementDTO';
import { IProjectUser } from '@interfaces/portal/projects';

const {
	portalTasks,
	portalTasksStatuses,
	portalTasksPriorities,
	portalTasksLinkTypes,
	portalTasksAssignees,
	portalTasksAssigneesFilter,
	portalTaskUploadUrlAttachment,
	portalCreateTask,
	portalDeleteTasks,
	portalTasksToLink,
	portalAvailableMilestones,
} = URL;

export const getPortalTasks = ({
	page,
	pageSize,
	sortDirection,
	sortFieldName,
	searchValue,
	status,
	priority,
	projectId,
	assignees,
	milestoneId,
}: IGetPortalTasksDTO): Promise<IItemsResponse<IPortalTaskDTO>> => {
	return client.get(portalTasks, {
		params: {
			page,
			pageSize,
			sortDirection,
			sortFieldName,
			searchValue,
			status,
			priority,
			projectId,
			assignees,
			milestoneId,
		},
		paramsSerializer: params =>
			qs.stringify(
				Object.fromEntries(
					Object.entries(params).filter(([_, value]) => value !== null && value !== undefined),
				),
				{ indices: false },
			),
	});
};

export const getPortalTasksStatuses = (projectId?: string): Promise<TNameTitle[]> => {
	return client.get(portalTasksStatuses, {
		params: {
			projectId,
		},
	});
};

export const getPortalTasksPriorities = (): Promise<TNameTitle[]> => {
	return client.get(portalTasksPriorities);
};

export const getPortalTasksLinkTypes = (): Promise<TNameTitle[]> => {
	return client.get(portalTasksLinkTypes);
};

export const getPortalTasksAssignees = (projectId: string): Promise<IProjectUser[]> => {
	return client.get(portalTasksAssignees, {
		params: {
			projectId,
		},
	});
};

export const getPortalTasksAssigneesForFilter = (projectId: string): Promise<IProjectUser[]> => {
	return client.get(portalTasksAssigneesFilter, {
		params: {
			projectId,
		},
	});
};

export const getTaskUploadURLAttachment = ({ fileName }: IDefaultUploadUrlApi) => {
	return client.post(portalTaskUploadUrlAttachment, {
		fileName,
	});
};

export const createTask = (data: ICreateTaskDTO): Promise<IPortalTaskDTO> => {
	return client.post(portalCreateTask, data);
};

export const editTask = ({ taskId, dto }: IEditTaskDTO): Promise<IPortalTaskDTO> => {
	return client.post(`${portalTasks}/${taskId}/update`, {
		...dto,
	});
};

export const getPortalTaskById = (id: string): Promise<IPortalTaskDTO> => {
	return client.get(`${portalTasks}/${id}`);
};

export const removeTasks = ({
	filter,
	ids,
	isAll,
}: IGroupOperationDTO<IDeleteTasksDTO | null>): Promise<void> => {
	return client.post(portalDeleteTasks, {
		filter,
		ids,
		isAll,
	});
};

export const getPortalTasksToLink = ({ taskId, projectId }: IGetTasksToLinkDTO): Promise<IPortalTaskToLinkDTO[]> => {
	return client.get(portalTasksToLink, {
		params: {
			id: taskId,
			projectId,
		},
	});
};

export const getPortalAvailableMilestones = (projectId: string): Promise<IPortalAvailableMilestonesDTO[]> => {
	return client.get(portalAvailableMilestones, {
		params: {
			projectId,
		},
	});
};

export const checkIsTaskExists = ({ taskId }: ICheckIsTaskExistsDTO): Promise<void> => {
	return client.get(`${portalTasks}/${taskId}/exist`);
};
